import { useState } from "react";
import { getDetails } from "use-places-autocomplete";
import { getPlaceAddress } from "@shared/lib/google";
import usePlacesAutocomplete from "@shared/hooks/usePlacesAutoComplete";
import Progress from "@ui-kit/feedback/Progress/Progress.web";
import ComboBox from "@ui-kit/form/ComboBox/ComboBox.web";
import ThresholdLoader from "@shared/components/ui-kit/feedback/ThresholdLoader/ThresholdLoader.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import type { SearchAddressComboBoxSharedProps } from "./SearchAddressComboBox.props";

export type SearchAddressComboBoxProps = SearchAddressComboBoxSharedProps & {
  //
};

export default function SearchAddressComboBox(
  props: SearchAddressComboBoxProps
): JSX.Element {
  const {
    id,
    placeholder,
    filterOptions,
    onSelectedItemChange,
    onInputValueChange,
    ariaLabel,
  } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    suggestions: { data: placesData },
    setValue: setPlacesValue,
  } = usePlacesAutocomplete({ filterOptions });

  return (
    <Flex direction="column" gap="2xs">
      <ComboBox
        id={`${id}-combo-box`}
        data-testid={`${id}-combo-box`}
        ariaLabel={ariaLabel || placeholder}
        placeholder={placeholder}
        dropdownVariant="secondary"
        items={placesData.slice(0, 2).map((place) => ({
          id: place.place_id,
          name: place.structured_formatting.main_text,
          secondaryText: place.structured_formatting.secondary_text,
        }))}
        // Google Places handles search/filtering — skip ComboBox client-side filter
        onLoadMore={() => undefined}
        onInputValueChange={(inputValue) => {
          if (onInputValueChange && inputValue) {
            onInputValueChange(inputValue);
          }
          if (inputValue) {
            setPlacesValue(inputValue);
          }
        }}
        onChange={async (selectedItem) => {
          if (!selectedItem?.id) {
            return;
          }
          setIsLoading(true);

          const result = await getDetails({
            placeId: selectedItem.id.toString(),
          });

          if ((result as google.maps.places.PlaceResult)?.address_components) {
            const address = getPlaceAddress(
              result as google.maps.places.PlaceResult
            );

            onSelectedItemChange(address);
            setIsLoading(false);
          }
        }}
      />

      <Box>
        <ThresholdLoader
          isLoading={isLoading}
          at2s={
            <Progress
              isIndeterminate
              size="xs"
              mt="-5px"
              borderRadius="sm"
              zIndex="tooltip"
            />
          }
        />
      </Box>
    </Flex>
  );
}
